import React, {useState, useEffect, useMemo} from 'react'
import useUpdateShopperContext from '../store-selector-modal/updateShopperContext'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import StoreAddress from '../store-address'
import {useSearchStores, useStores} from '@salesforce/commerce-sdk-react'
import {GET_STORE_LIMIT, GET_STORE_MAX_DISTANCE} from '../../constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {pxToRem} from '../../utills/pxToRem'
import PropTypes from 'prop-types'

function StoreDetails({isPLP}) {
    const {updateShopperContext, shopperContext, isShopperContextLoading} =
        useUpdateShopperContext()
    const {data: customer} = useCurrentCustomer()
    const {site} = useMultiSite()
    const [location, setLocation] = useState({lat: null, lng: null})

    const shopperStoreDetails = useMemo(() => {
        if (shopperContext?.customQualifiers?.storeDetails) {
            return JSON.parse(shopperContext.customQualifiers.storeDetails)
        }
        return null
    }, [shopperContext])

    // Get store details
    const {data: prefereredStoreDetails} = useStores(
        {
            parameters: {
                siteId: site.id,
                ids: customer.c_preferredStore
            }
        },
        {
            enabled:
                !!customer.c_preferredStore &&
                (!shopperStoreDetails || shopperStoreDetails?.isGeolocation)
        }
    )

    const isValidStore = (store) => store && Object.keys(store).length > 0
    const getStoreDetails = (storeData) => {
        const store = storeData?.[0]
        if (!store || !store.id) return null
        const {name, address1, city, countryCode, stateCode, postalCode, id} = store
        return {id, name, address1, city, stateCode, countryCode, postalCode}
    }

    const updateStoreContext = (storeDetails) => {
        if (!storeDetails) return
        updateShopperContext(
            storeDetails.id,
            JSON.stringify({
                ...storeDetails,
                customerType: customer.customerType,
                isGeolocation: true
            })
        )
    }

    useEffect(() => {
        if (
            'geolocation' in navigator &&
            !shopperStoreDetails &&
            !(prefereredStoreDetails?.data?.length >= 1)
        ) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    })
                },
                (err) => {
                    console.log('Error:', err)
                }
            )
        }
    }, [shopperStoreDetails, prefereredStoreDetails?.data])

    const {data: nearestStoreDetails} = useSearchStores(
        {
            parameters: {
                latitude: location.lat,
                siteId: site.id,
                longitude: location.lng,
                distanceUnit: 'mi',
                limit: GET_STORE_LIMIT,
                maxDistance: GET_STORE_MAX_DISTANCE
            }
        },
        {
            enabled: location.lat !== null && location.lng !== null && !shopperStoreDetails
        }
    )

    useEffect(() => {
        if (isShopperContextLoading) return
        if (customer.customerType === 'guest') {
            const isShopperStoreInvalid = !isValidStore(shopperStoreDetails)
            const nearestStore = getStoreDetails(nearestStoreDetails?.data)
            if (isShopperStoreInvalid && nearestStore) {
                updateStoreContext(nearestStore)
            }
        } else {
            const shopperStoreExists = isValidStore(shopperStoreDetails)
            const preferStore = getStoreDetails(prefereredStoreDetails?.data)
            const nearestStore = getStoreDetails(nearestStoreDetails?.data)
            if (shopperStoreExists && shopperStoreDetails?.isGeolocation) {
                updateStoreContext(preferStore || nearestStore)
            } else if (!shopperStoreExists) {
                updateStoreContext(preferStore || nearestStore)
            }
        }
    }, [customer.customerType, shopperStoreDetails, prefereredStoreDetails, nearestStoreDetails])

    if (isPLP) {
        if (shopperStoreDetails && Object.keys(shopperStoreDetails)?.length > 0) {
            return (
                <StoreAddress
                    flex="1"
                    marginRight={pxToRem(10)}
                    storeDetails={shopperStoreDetails}
                />
            )
        }
        return
    } else {
        return (
            <Box>
                <StoreAddress storeDetails={shopperStoreDetails} />
            </Box>
        )
    }
}

StoreDetails.propTypes = {
    isPLP: PropTypes.bool
}

export default StoreDetails
